import {
  IonButton,
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonImg,
  IonInput,
  IonSpinner
} from '@ionic/react';
import { HKPlatform } from 'core/constants';
import { RootState } from 'core/store';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './DigitalCatalogDetails.scss';
import Close from 'assets/icons/close.svg';
import HKSelect from 'components/hk-select/HKSelect';
import ReactPlayer from 'react-player';
import {
  clearCurrentCatalogProduct,
  hideCatalogModal,
  resetDigitalCatalogTaskDetails,
  setDigitalCatalogTaskLoading
} from 'features/digital/store/DigitalCatalogSlice';
import { cameraOutline, alertOutline } from 'ionicons/icons';
import { usePhotoGallery, UserPhoto } from 'core/hooks/usePhotoGallery';
import {
  createCatalogProduct,
  fetchCatalogProductDetails,
  fetchCatalogProducts,
  fetchCatalogProductTemplate,
  fetchCatalogTask,
  fetchCatalogTaskStatusTags,
  patchCatalogTaskDetails,
  updateCatalogProduct
} from 'features/digital/store/DigitalCatalogActions';
import { useForm } from 'react-hook-form';
import {
  getFirebaseDownloadUrl,
  useFirebaseUpload
} from 'core/hooks/useFirebase';
import { displayError, displaySuccess } from 'core/services/AlertActions';
import { gaBtnClickEvent, gaModalView } from 'core/util';
import {
  AttributeSchema,
  DigitalProduct,
  ProductBodyDetails,
  ProductModelSearchResult,
  TaskBodyDetails
} from 'core/types';
import ProductModelSerialStepFields from 'features/shared/inventory/components/ProductModelSerialStepFields';
import ProductBrandStepField from 'features/shared/inventory/components/ProductBrandStepField';
import { searchManufacturers } from 'features/shared/inventory/store/InventoryActions';
import { resetManufacturers } from 'features/shared/inventory/store/InventorySlice';

export enum ProductAttributeType {
  STRING = 'string',
  TAGGED = 'tagged'
}

export enum PhotoType {
  MODEL = 'model',
  BRAND = 'brand'
}

// Asset `name` values assigned by avid-homebook-api when catalog photos are
// saved (see site_constants.py MODEL_PHOTO_NAME / BRAND_PHOTO_NAME). The API
// stores both photos as type_id=3 assets with NO ordering guarantee and appends
// a fresh row on every save, so the current photo must be matched by name
// (newest id wins) rather than by assets[] index — assets[0]/[1] is incidental.
const MODEL_PHOTO_ASSET_NAME = 'Model #/Serial # Photo';
const BRAND_PHOTO_ASSET_NAME = 'Brand Photo';

const findLatestAssetByName = (assets: any[], name: string) =>
  [...(assets ?? [])]
    .filter((asset) => asset?.name === name)
    .sort((a, b) => b.id - a.id)[0];

const DigitalCatalogDetails: React.FC = () => {
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const { isDigital } = useSelector((state: RootState) => state.user);
  const { loading } = useSelector((state: RootState) => state.loading);
  const {
    taskLoading,
    catalogTasksList,
    catalogTaskId,
    catalogTaskProductTypeId,
    catalogTaskDetails,
    catalogTaskStatusTags,
    currentCatalogProduct,
    productTemplate,
    productsList
  } = useSelector((state: RootState) => state.digitalCatalog);
  const { manufacturers } = useSelector((state: RootState) => state.inventory);

  const { takePhoto } = usePhotoGallery();
  const { uploadFile } = useFirebaseUpload();
  const [isTitleVisible, setIsTitleVisible] = useState(true);
  const [modelPhoto, setModelPhoto] = useState<UserPhoto | null>();
  const [brandPhoto, setBrandPhoto] = useState<UserPhoto | null>();
  const [selectedProductTags, setSelectedProductTags] = useState<any[]>([]);
  const [selectedStatusTags, setSelectedStatusTags] = useState<number[]>([]);
  const [availableProducts, setAvailableProducts] = useState<DigitalProduct[]>(
    []
  );
  const [selectedProductId, setSelectedProductId] = useState<any>();
  // Mirrors InventoryAddProduct.tsx's Step 1/2 catalog-tie-in fields (Model
  // Number search-select + Brand catalog-select) — same precedence rule:
  // product_template_id (Model #) wins over manufacturer_id (Brand) if both
  // are picked.
  const [selectedProductTemplateId, setSelectedProductTemplateId] = useState<
    number | null
  >(null);
  const [selectedProductTemplateLabel, setSelectedProductTemplateLabel] =
    useState<string | null>(null);
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<
    number | null
  >(null);
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const userId = useSelector((state: RootState) => state.user.id);

  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { isDirty }
  } = useForm();

  const handleScroll = (event: any) => {
    if (event.detail.scrollTop > 0) {
      setIsTitleVisible(false);
    } else {
      setIsTitleVisible(true);
    }
  };

  const availableProductOptions = availableProducts.map(
    (product: DigitalProduct, i: number) => ({
      value: product.id,
      label: `${product.type.replace(/_/g, ' ')} ${i + 1}`
    })
  );

  const selectProduct = (value: string | number | null) => {
    gaBtnClickEvent('select_product');
    setSelectedProductId(value);
  };

  const selectProductTag = (field: string, tag: any) => {
    const keyExists = selectedProductTags.some((item: any) => {
      return field in item;
    });
    if (keyExists) {
      if (selectedProductTags.every((item: any) => item[field] === tag)) {
        setSelectedProductTags([]);
      } else {
        const existingTagIndex = selectedProductTags.findIndex((item: any) => {
          return field in item;
        });
        setSelectedProductTags(selectedProductTags.splice(existingTagIndex, 1));
        setSelectedProductTags([...selectedProductTags, { [field]: tag }]);
      }
    } else {
      setSelectedProductTags([...selectedProductTags, { [field]: tag }]);
    }
  };

  const selectStatusTag = (tagId: number) => {
    if (selectedStatusTags.includes(tagId)) {
      setSelectedStatusTags(selectedStatusTags.filter((id) => id !== tagId));
    } else {
      setSelectedStatusTags([...selectedStatusTags, tagId]);
    }
  };

  const checkIfProductTagSelected = (field: string, tag: any) => {
    return selectedProductTags.some((item: any) => {
      return item[field] === tag;
    });
  };

  const checkIfTaskEdited = () => {
    let isEdited = false;
    if (catalogTaskDetails?.finished !== null) {
      if (
        isDirty ||
        !!selectedProductTags.length ||
        !!selectedStatusTags.length ||
        !!modelPhoto ||
        !!brandPhoto
      ) {
        isEdited = true;
      }
    }
    return isEdited;
  };

  const checkIfLastCatalogTask = () => {
    const unfinishedTasks = catalogTasksList.filter(
      (task) => task.finished === null
    );
    return unfinishedTasks.length === 1;
  };

  const hasTakePhotoStep = (name: string) => {
    return (
      name.toLowerCase().includes('take') ||
      name.toLowerCase().includes('catalog')
    );
  };

  const addPhoto = (type: string) => {
    gaBtnClickEvent('catalog_add_photo');
    takePhoto()
      .then((photo) => {
        if (!!homeId && !!photo.filePath && !!photo.webviewPath) {
          photo.filePath = `product_${new Date().getTime()}_catalog_${
            type === PhotoType.MODEL ? '1' : '2'
          }.jpg`;
          if (type === PhotoType.MODEL) {
            setModelPhoto(photo);
          } else if (type === PhotoType.BRAND) {
            setBrandPhoto(photo);
          }
        }
      })
      .catch((error) => {
        console.log(`Upload Photo Error: ${error}`);
      });
  };

  const uploadPhotos = async () => {
    dispatch(setDigitalCatalogTaskLoading(true));
    const uploads: any[] = [];
    if (!!homeId && !!modelPhoto) {
      uploads.push(
        uploadFile(homeId, modelPhoto.filePath, modelPhoto.imageData).catch(
          (e) => {
            dispatch(setDigitalCatalogTaskLoading(false));
            displayError(e);
            throw e;
          }
        )
      );
    }
    if (!!homeId && !!brandPhoto) {
      uploads.push(
        uploadFile(homeId, brandPhoto.filePath, brandPhoto.imageData).catch(
          (e) => {
            dispatch(setDigitalCatalogTaskLoading(false));
            displayError(e);
            throw e;
          }
        )
      );
    }
    return await Promise.all(uploads);
  };

  const addProduct = (formData: any) => {
    gaBtnClickEvent('catalog_add_another');
    let promises: any[] = [];
    const productBody: ProductBodyDetails = {
      home_id: homeId,
      type: productTemplate.field,
      attributes: {}
    };

    // "Add Another" is enabled whenever either a fresh Model Number photo has
    // been taken OR an existing product is selected in the "Select Product"
    // dropdown. Only clear-and-bail when nothing new was captured on top of
    // that selection — a selected product plus a freshly-taken photo/filled
    // field should fall through and create the new item, not silently
    // discard what the user just entered.
    //
    // Selecting an existing product auto-fills selectedProductTemplateId/
    // selectedManufacturerId/serial from that product's own data (see the
    // currentCatalogProduct effect above) — so comparing those fields against
    // a bare truthy check always looks like "new input" for any existing
    // product that already has a template/manufacturer/serial, even when the
    // user changed nothing. Compare against the selected product's original
    // values instead, so only a genuine edit on top of the selection counts.
    //
    // currentCatalogProduct is only trusted here when its id matches
    // selectedProductId — it can otherwise still be mid-fetch/stale (e.g. the
    // brief window right after switching selections, before the effect above
    // clears/repopulates it), and comparing against a different product's
    // details would wrongly flag either a false "new input" or a false clear.
    const selectedProductDetails =
      currentCatalogProduct?.id === selectedProductId
        ? currentCatalogProduct
        : null;
    const hasNewInput =
      !!modelPhoto ||
      !!brandPhoto ||
      selectedProductTemplateId !==
        (selectedProductDetails?.product_template?.id ?? null) ||
      selectedManufacturerId !==
        (selectedProductDetails?.product_template?.manufacturer?.id ?? null) ||
      (formData.serial || '') !== (selectedProductDetails?.serial || '');
    if (!!selectedProductId && !hasNewInput) {
      resetProductForm(formData);
      dispatch(clearCurrentCatalogProduct());
      return;
    }

    uploadPhotos().then(
      (uploadedImages) => {
        for (const image of uploadedImages) {
          promises.push(
            getFirebaseDownloadUrl(image.metadata.fullPath).then((url) => {
              if (image.metadata.name.toLowerCase().includes('catalog_1')) {
                productBody['model_photo'] = url;
              }
              if (image.metadata.name.toLowerCase().includes('catalog_2')) {
                productBody['brand_photo'] = url;
              }
            })
          );
        }

        // Create TAGGED attributes from availableProductTags for POST body
        if (selectedProductTags.length > 0) {
          selectedProductTags.forEach((tag) => {
            for (const [key, value] of Object.entries(tag)) {
              productBody.attributes[key] = [value];
            }
          });
        }

        // Create STRING attributes from formData for POST body — "serial"
        // is excluded here since it's the dedicated Step 1 field below,
        // mapped directly to Products.serial rather than a dynamic attribute.
        if (!!productTemplate?.product_attribute_schema.length) {
          productTemplate.product_attribute_schema
            .filter(
              (question: AttributeSchema) =>
                question.field.toLowerCase() !== 'serial'
            )
            .forEach((question: AttributeSchema) => {
              Object.entries(formData).forEach((datum) => {
                const [key, value] = datum;
                if (question.field === key) {
                  productBody.attributes[question.field] = value;
                }
              });
            });
        }

        // Serial Number (Step 1) maps directly to Products.serial, not a
        // dynamic attribute — see ProductBodyDetails.serial.
        if (formData.serial) {
          productBody.serial = formData.serial;
        }

        // Model Number (Step 1) wins over Brand (Step 2) if both are
        // picked — it's the more specific catalog selection.
        if (selectedProductTemplateId) {
          productBody.product_template_id = selectedProductTemplateId;
        } else if (selectedManufacturerId) {
          productBody.manufacturer_id = selectedManufacturerId;
        }

        // Create Product Here AND RESET FORM HERE
        if (!!homeId && !!productBody) {
          Promise.all(promises)
            .then(async () => {
              await dispatch(createCatalogProduct(homeId, productBody));
              resetProductForm(formData);
              dispatch(displaySuccess('Product Added', 'Success'));
              dispatch(fetchCatalogProducts(homeId));
              dispatch(setDigitalCatalogTaskLoading(false));
            })
            .catch((error: any) => {
              dispatch(setDigitalCatalogTaskLoading(false));
              console.log(error);
            });
        }
      },
      (error) => {
        dispatch(displayError(error));
      }
    );
  };

  const completeTask = (formData: any) => {
    gaBtnClickEvent('catalog_complete_task');
    let firebasePromises: any[] = [];
    const taskBody: TaskBodyDetails = {};
    const productBody: ProductBodyDetails = {
      home_id: homeId,
      type: productTemplate.field,
      attributes: {}
    };

    // Create PHOTO for POST body (Upload to firebase storage)
    uploadPhotos().then((uploadedImages) => {
      for (const image of uploadedImages) {
        firebasePromises.push(
          getFirebaseDownloadUrl(image.metadata.fullPath).then((url) => {
            if (image.metadata.name.toLowerCase().includes('catalog_1')) {
              productBody['model_photo'] = url;
            }
            if (image.metadata.name.toLowerCase().includes('catalog_2')) {
              productBody['brand_photo'] = url;
            }
          })
        );
      }

      Promise.all(firebasePromises)
        .then(() => {
          // Create TAGGED attributes from availableProductTags for POST body
          if (selectedProductTags.length > 0) {
            selectedProductTags.forEach((tag) => {
              for (const [key, value] of Object.entries(tag)) {
                productBody.attributes[key] = [value];
              }
            });
          }

          // Create STRING attributes from formData for POST body — "serial"
          // is excluded here since it's the dedicated Step 1 field below,
          // mapped directly to Products.serial rather than a dynamic attribute.
          if (!!productTemplate?.product_attribute_schema.length) {
            productTemplate.product_attribute_schema
              .filter(
                (question: any) => question.field.toLowerCase() !== 'serial'
              )
              .forEach((question: any) => {
                Object.entries(formData).forEach((datum) => {
                  const [key, value] = datum;
                  if (question.field === key) {
                    productBody.attributes[question.field] = value;
                  }
                });
              });
          }

          // Serial Number (Step 1) maps directly to Products.serial, not a
          // dynamic attribute — see ProductBodyDetails.serial.
          if (formData.serial) {
            productBody.serial = formData.serial;
          }

          // Model Number (Step 1) wins over Brand (Step 2) if both are
          // picked — it's the more specific catalog selection.
          if (selectedProductTemplateId) {
            productBody.product_template_id = selectedProductTemplateId;
          } else if (selectedManufacturerId) {
            productBody.manufacturer_id = selectedManufacturerId;
          }
          // Persist the product FIRST, then mark the task finished only after
          // that save resolves — the task PATCH must not run in parallel with
          // the product save. Default to a no-op when there is no product to
          // save, so the task PATCH still runs unchanged in that case.
          let persistProduct: Promise<unknown> = Promise.resolve();
          if (!!homeId && !!productBody) {
            if (!!selectedProductId) {
              // Existing product selected → update it (do NOT create a new one).
              persistProduct = dispatch(
                updateCatalogProduct(homeId, selectedProductId, productBody)
              );
            } else {
              // No product selected → create a new one.
              persistProduct = dispatch(
                createCatalogProduct(homeId, productBody)
              );
            }
          }

          // taskLoading stays true until the product save and, in turn, the task
          // patch settle, so the form can't be interacted with or resubmitted
          // while either request is still in flight.
          persistProduct
            .then(() => {
              // Catalog Task Submission (DONE)
              if (!!homeId && !!catalogTaskId) {
                taskBody.finished = new Date();
                taskBody.finished_by_id = userId;
                taskBody.tag_ids = selectedStatusTags;
                taskBody.notes = formData.notes;
                return dispatch(
                  patchCatalogTaskDetails(homeId, catalogTaskId, taskBody)
                );
              }
            })
            .then(() => {
              const completedMsg =
                catalogTaskDetails?.finished !== null
                  ? 'Task Updated'
                  : 'Task Completed';
              if (!checkIfLastCatalogTask()) {
                dispatch(displaySuccess(completedMsg, 'Success'));
              }
              // hideCatalogModal handles teardown on success; only the failure
              // path needs to release the loading gate back to the form.
              dispatch(hideCatalogModal());
            })
            .catch((error: any) => {
              dispatch(setDigitalCatalogTaskLoading(false));
              console.log(error);
            });
        })
        .catch((error: any) => {
          dispatch(setDigitalCatalogTaskLoading(false));
          console.log(error);
        });
    });
  };

  const noItem = (notes: any) => {
    gaBtnClickEvent('catalog_no_item');

    const taskBody: TaskBodyDetails = {};
    if (!!homeId && !!catalogTaskId) {
      taskBody.finished = new Date();
      taskBody.finished_by_id = userId;
      taskBody.tag_ids = selectedStatusTags;
      taskBody.notes = notes;
      dispatch(patchCatalogTaskDetails(homeId, catalogTaskId, taskBody))
        .then(() => {
          dispatch(hideCatalogModal());
        })
        .catch((error: any) => {
          dispatch(setDigitalCatalogTaskLoading(false));
          console.log(error);
        });
    }
  };

  const resetProductForm = (formData: any) => {
    setModelPhoto(null);
    setBrandPhoto(null);
    setSelectedProductId(null);
    setSelectedProductTemplateId(null);
    setSelectedProductTemplateLabel(null);
    setSelectedManufacturerId(null);
    Object.entries(formData).forEach((datum) => {
      const [key] = datum;
      if (key !== 'notes') {
        setValue(key, '');
      }
    });
  };

  // Guards the latest-product auto-select below so it only fires once per
  // task open — reset whenever a (new or the same) task is (re)loaded, so a
  // later productsList refetch (e.g. after "Add Another" explicitly clears
  // the selection to start a fresh product) doesn't clobber that reset back
  // to the just-added product.
  const hasAutoSelectedProductRef = useRef(false);

  useEffect(() => {
    dispatch(resetDigitalCatalogTaskDetails());
    hasAutoSelectedProductRef.current = false;
    setSelectedProductId(null);
    if (!!homeId && !!catalogTaskId && !!catalogTaskProductTypeId) {
      dispatch(fetchCatalogTask(homeId, catalogTaskId));
      dispatch(fetchCatalogTaskStatusTags());
      dispatch(fetchCatalogProductTemplate(catalogTaskProductTypeId));
      dispatch(fetchCatalogProducts(homeId));
    }
  }, [dispatch, homeId, catalogTaskId, catalogTaskProductTypeId]);

  useEffect(() => {
    if (!!catalogTaskDetails) {
      setValue('notes', catalogTaskDetails.notes);
      setSelectedStatusTags(
        catalogTaskDetails.status_tags.map((tag: any) => tag.id)
      );
    }
  }, [catalogTaskDetails]);

  useEffect(() => {
    if (!!productsList) {
      const matchingProductsList = productsList.filter(
        (product: any) => product.type_id === catalogTaskProductTypeId
      );
      setAvailableProducts(matchingProductsList);
      // Default the "Select Product" picker to the most-recently-added
      // product for this task's type, rather than requiring a manual pick.
      // productsList is sorted ascending by id (fetchDigitalProducts), so
      // the last entry in the (order-preserving) filtered list is latest.
      if (!hasAutoSelectedProductRef.current && matchingProductsList.length) {
        const latestIndex = matchingProductsList.length - 1;
        const latestProduct = matchingProductsList[latestIndex];
        setSelectedProductId(latestProduct.id);
      }
      hasAutoSelectedProductRef.current = true;
    }
  }, [productsList]);

  useEffect(() => {
    // Clear first — otherwise currentCatalogProduct keeps holding whichever
    // product was previously selected (resetProductForm only clears
    // selectedProductId, not this) while the fetch for the newly selected
    // product is still in flight, which would let addProduct's hasNewInput
    // check below compare against the wrong product's details. Also clear
    // the prefilled fields themselves — the prefill effect below only ever
    // populates them from a truthy currentCatalogProduct, so it never clears
    // them back out on its own, and they'd otherwise keep showing the
    // previous product's Model Number/Brand/Serial for the whole fetch gap
    // (or indefinitely, if the fetch errors).
    dispatch(clearCurrentCatalogProduct());
    setSelectedProductTemplateId(null);
    setSelectedProductTemplateLabel(null);
    setSelectedManufacturerId(null);
    setValue('serial', '');
    if (!!homeId && catalogTaskProductTypeId && !!selectedProductId) {
      dispatch(fetchCatalogProductDetails(homeId, Number(selectedProductId)));
    }
  }, [selectedProductId, homeId, catalogTaskProductTypeId, dispatch]);

  // Prefills the Model Number/Serial Number/Brand fields from whichever
  // product the "Select Product" dropdown currently has selected — mirrors
  // useProductCatalogEditFields' initialize() (InventoryProductEditForm.tsx),
  // which does the same product_template.id/model/manufacturer.id -> field
  // mapping for the equivalent inventory edit form. selectedManufacturerId
  // (not the manufacturer's name) is what HKSelect's `value`
  // binds to — the name still displays, via the option whose value matches
  // this id.
  useEffect(() => {
    // currentCatalogProduct's fetch (fetchCatalogProductDetails) has no
    // request sequencing — if selectedProductId changes again before an
    // in-flight fetch resolves, an older response can land after a newer
    // one and overwrite currentCatalogProduct with a mismatched product.
    // Only prefill from it when its id still matches the current selection.
    if (
      !!currentCatalogProduct &&
      currentCatalogProduct.id === selectedProductId
    ) {
      setSelectedProductTemplateId(
        currentCatalogProduct.product_template?.id ?? null
      );
      setSelectedProductTemplateLabel(
        currentCatalogProduct.product_template?.model ?? null
      );
      setSelectedManufacturerId(
        currentCatalogProduct.product_template?.manufacturer?.id ?? null
      );
      setValue('serial', currentCatalogProduct.serial || '');
    }
  }, [currentCatalogProduct, selectedProductId]);

  // Scopes the Brand catalog-select's options to this task's product type —
  // mirrors InventoryAddProduct.tsx's equivalent effect on selectedProductType.
  useEffect(() => {
    dispatch(resetManufacturers());
    if (!!catalogTaskProductTypeId) {
      dispatch(searchManufacturers(catalogTaskProductTypeId));
    }
  }, [catalogTaskProductTypeId]);

  useEffect(() => {
    gaModalView(isDigital, '/catalog/task');
  }, []);

  function CurrentCatalogQuestions() {
    // Match photos by asset name (newest wins), not by array index — the API
    // doesn't guarantee assets[0]=model / assets[1]=brand.
    const modelAsset = findLatestAssetByName(
      currentCatalogProduct?.assets,
      MODEL_PHOTO_ASSET_NAME
    );
    const brandAsset = findLatestAssetByName(
      currentCatalogProduct?.assets,
      BRAND_PHOTO_ASSET_NAME
    );
    return (
      <>
        {!!catalogTaskDetails?.name &&
          !!currentCatalogProduct &&
          hasTakePhotoStep(catalogTaskDetails.name) && (
            // Photo Questions
            <div className="hk-digital-catalog-details-body-steps">
              <div className="hk-digital-catalog-details-body-steps-card">
                <div className="hk-digital-catalog-details-body-steps-card-title">
                  1: Take a picture of Model Number and Serial Number Details
                </div>
                <div className="hk-digital-catalog-details-body-steps-card-subtitle">
                  <IonIcon icon={alertOutline} />
                  <span>required task</span>
                </div>
                <div className="hk-digital-catalog-details-body-steps-card-description">
                  <span>
                    This can be found on the inside or outside of the{' '}
                    {!!productTemplate?.label
                      ? productTemplate?.label
                      : 'product'}
                    .
                  </span>
                  <div className="hk-digital-catalog-details-body-steps-card-description-image">
                    {!!modelAsset && !modelPhoto && (
                      <>
                        <IonImg src={modelAsset.file_url} />
                      </>
                    )}
                    {!!modelPhoto && (
                      <>
                        <IonImg src={modelPhoto.webviewPath} />
                        <input
                          name="model_photo"
                          ref={register}
                          type="hidden"
                          value={modelPhoto.webviewPath}
                        />
                      </>
                    )}
                  </div>
                  <IonButton
                    className="hk-digital-catalog-details-body-steps-card-description-button"
                    onClick={() => addPhoto(PhotoType.MODEL)}
                  >
                    <IonIcon color="dark" icon={cameraOutline} />
                    {'Replace Photo'}
                  </IonButton>
                  <ProductModelSerialStepFields
                    classPrefix="hk-digital-catalog-details"
                    typeId={catalogTaskProductTypeId}
                    value={selectedProductTemplateId}
                    valueLabel={selectedProductTemplateLabel}
                    register={register}
                    onModelChange={(result: ProductModelSearchResult) => {
                      setSelectedProductTemplateId(result.id);
                      setSelectedProductTemplateLabel(result.model);
                      // Brand comes off the result as a real manufacturers.id —
                      // no name matching against the loaded options list.
                      setSelectedManufacturerId(result.manufacturer_id);
                    }}
                  />
                </div>
              </div>
              <div className="hk-digital-catalog-details-body-steps-card">
                <div className="hk-digital-catalog-details-body-steps-card-title">
                  2: Take a picture of Brand / Manufacturer.
                </div>
                <div className="hk-digital-catalog-details-body-steps-card-description">
                  <span>
                    Typically found on the front or back of the{' '}
                    {!!productTemplate?.label
                      ? productTemplate?.label
                      : 'product'}
                    .
                  </span>
                  <div className="hk-digital-catalog-details-body-steps-card-description-image">
                    {!!brandAsset && !brandPhoto && (
                      <>
                        <IonImg src={brandAsset.file_url} />
                      </>
                    )}
                    {!!brandPhoto && (
                      <>
                        <IonImg src={brandPhoto.webviewPath} />
                        <input
                          name="brand_photo"
                          ref={register}
                          type="hidden"
                          value={brandPhoto.webviewPath}
                        />
                      </>
                    )}
                  </div>
                  <IonButton
                    className="hk-digital-catalog-details-body-steps-card-description-button"
                    onClick={() => addPhoto(PhotoType.BRAND)}
                  >
                    <IonIcon color="dark" icon={cameraOutline} />
                    {'Replace Photo'}
                  </IonButton>
                  <ProductBrandStepField
                    classPrefix="hk-digital-catalog-details"
                    value={selectedManufacturerId}
                    manufacturers={manufacturers}
                    onChange={(value) => {
                      setSelectedManufacturerId(value);
                      if (value !== null) {
                        setSelectedProductTemplateId(null);
                        setSelectedProductTemplateLabel(null);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        {!!productTemplate?.product_attribute_schema.length &&
          productTemplate.product_attribute_schema
            .filter(
              (attribute: any) => attribute.field.toLowerCase() !== 'serial'
            )
            .map((attribute: any, i: number) => {
              return (
                // Tagged Questions
                <div key={`attribute-${i}`}>
                  {attribute.type === ProductAttributeType.TAGGED ? (
                    <div
                      className="hk-digital-catalog-details-body-steps-card"
                      key={`tagged-question-${i}`}
                    >
                      <div className="hk-digital-catalog-details-body-steps-card-title">
                        {`${
                          !!catalogTaskDetails?.name &&
                          hasTakePhotoStep(catalogTaskDetails.name)
                            ? i + 2
                            : i + 1
                        }:`}{' '}
                        {attribute.label}
                      </div>
                      <div className="hk-digital-catalog-details-body-steps-card-description">
                        <div className="product-tag-container">
                          {!!attribute.allow &&
                            attribute.allow.map((tag: any, i: number) => (
                              <div
                                className={`product-tag ${
                                  checkIfProductTagSelected(
                                    attribute.field,
                                    tag
                                  )
                                    ? 'product-tag-selected'
                                    : ''
                                }`}
                                key={`product-tag-${i}`}
                                onClick={() =>
                                  selectProductTag(attribute.field, tag)
                                }
                              >
                                {tag}
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Text Input Questions
                    <div
                      className="hk-digital-catalog-details-body-steps-card"
                      key={`input-question-${i}`}
                    >
                      <div className="hk-digital-catalog-details-body-steps-card-title">
                        {`${
                          !!catalogTaskDetails?.name &&
                          hasTakePhotoStep(catalogTaskDetails.name)
                            ? i + 3
                            : i + 1
                        }:`}{' '}
                        {attribute.label}
                      </div>
                      <div className="hk-digital-catalog-details-body-steps-card-description">
                        <IonInput
                          className="hk-digital-catalog-details-input"
                          placeholder="-"
                          readonly
                        ></IonInput>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
      </>
    );
  }

  function CatalogTaskQuestions() {
    return (
      <>
        {!!catalogTaskDetails?.name &&
          hasTakePhotoStep(catalogTaskDetails.name) && (
            // Photo Questions
            <div className="hk-digital-catalog-details-body-steps">
              <div className="hk-digital-catalog-details-body-steps-card">
                <div className="hk-digital-catalog-details-body-steps-card-title">
                  1: Take a picture of Model Number and Serial Number Details
                </div>
                <div className="hk-digital-catalog-details-body-steps-card-subtitle">
                  <IonIcon icon={alertOutline} />
                  <span>required task</span>
                </div>
                <div className="hk-digital-catalog-details-body-steps-card-description">
                  <span>
                    This can be found on the inside or outside of the{' '}
                    {!!productTemplate?.label
                      ? productTemplate?.label
                      : 'product'}
                    .
                  </span>
                  <div className="hk-digital-catalog-details-body-steps-card-description-image">
                    {!!modelPhoto && (
                      <>
                        <IonImg src={modelPhoto.webviewPath} />
                        <input
                          name="model_photo"
                          ref={register}
                          type="hidden"
                          value={modelPhoto.webviewPath}
                        />
                      </>
                    )}
                  </div>
                  <IonButton
                    className="hk-digital-catalog-details-body-steps-card-description-button"
                    onClick={() => addPhoto(PhotoType.MODEL)}
                  >
                    <IonIcon color="dark" icon={cameraOutline} />
                    {!modelPhoto ? 'Add Photo' : 'Replace Photo'}
                  </IonButton>
                  <ProductModelSerialStepFields
                    classPrefix="hk-digital-catalog-details"
                    typeId={catalogTaskProductTypeId}
                    value={selectedProductTemplateId}
                    valueLabel={selectedProductTemplateLabel}
                    register={register}
                    onModelChange={(result: ProductModelSearchResult) => {
                      setSelectedProductTemplateId(result.id);
                      setSelectedProductTemplateLabel(result.model);
                      // Brand comes off the result as a real manufacturers.id —
                      // no name matching against the loaded options list.
                      setSelectedManufacturerId(result.manufacturer_id);
                    }}
                  />
                </div>
              </div>
              <div className="hk-digital-catalog-details-body-steps-card">
                <div className="hk-digital-catalog-details-body-steps-card-title">
                  2: Take a picture of Brand / Manufacturer.
                </div>
                <div className="hk-digital-catalog-details-body-steps-card-description">
                  <span>
                    Typically found on the front or back of the{' '}
                    {!!productTemplate?.label
                      ? productTemplate?.label
                      : 'product'}
                    .
                  </span>
                  <div className="hk-digital-catalog-details-body-steps-card-description-image">
                    {!!brandPhoto && (
                      <>
                        <IonImg src={brandPhoto.webviewPath} />
                        <input
                          name="brand_photo"
                          ref={register}
                          type="hidden"
                          value={brandPhoto.webviewPath}
                        />
                      </>
                    )}
                  </div>
                  <IonButton
                    className="hk-digital-catalog-details-body-steps-card-description-button"
                    onClick={() => addPhoto(PhotoType.BRAND)}
                  >
                    <IonIcon color="dark" icon={cameraOutline} />
                    {!brandPhoto ? 'Add Photo' : 'Replace Photo'}
                  </IonButton>
                  <ProductBrandStepField
                    classPrefix="hk-digital-catalog-details"
                    value={selectedManufacturerId}
                    manufacturers={manufacturers}
                    onChange={(value) => {
                      setSelectedManufacturerId(value);
                      if (value !== null) {
                        setSelectedProductTemplateId(null);
                        setSelectedProductTemplateLabel(null);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        {!!productTemplate?.product_attribute_schema.length &&
          productTemplate.product_attribute_schema
            .filter(
              (attribute: any) => attribute.field.toLowerCase() !== 'serial'
            )
            .map((attribute: any, i: number) => {
              return (
                // Tagged Questions
                <div key={`attribute-${i}`}>
                  {attribute.type === ProductAttributeType.TAGGED ? (
                    <div
                      className="hk-digital-catalog-details-body-steps-card"
                      key={`tagged-question-${i}`}
                    >
                      <div className="hk-digital-catalog-details-body-steps-card-title">
                        {`${
                          !!catalogTaskDetails?.name &&
                          hasTakePhotoStep(catalogTaskDetails.name)
                            ? i + 2
                            : i + 1
                        }:`}{' '}
                        {attribute.label}
                      </div>
                      <div className="hk-digital-catalog-details-body-steps-card-description">
                        <div className="product-tag-container">
                          {!!attribute.allow &&
                            attribute.allow.map((tag: any, i: number) => (
                              <div
                                className={`product-tag ${
                                  checkIfProductTagSelected(
                                    attribute.field,
                                    tag
                                  )
                                    ? 'product-tag-selected'
                                    : ''
                                }`}
                                key={`product-tag-${i}`}
                                onClick={() =>
                                  selectProductTag(attribute.field, tag)
                                }
                              >
                                {tag}
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Text Input Questions
                    <div
                      className="hk-digital-catalog-details-body-steps-card"
                      key={`input-question-${i}`}
                    >
                      <div className="hk-digital-catalog-details-body-steps-card-title">
                        {`${
                          !!catalogTaskDetails?.name &&
                          hasTakePhotoStep(catalogTaskDetails.name)
                            ? i + 3
                            : i + 1
                        }:`}{' '}
                        {attribute.label}
                      </div>
                      <div className="hk-digital-catalog-details-body-steps-card-description">
                        <IonInput
                          name={attribute.field}
                          ref={register}
                          className="hk-digital-catalog-details-input"
                          placeholder={`Enter ${attribute.label}...`}
                        ></IonInput>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
      </>
    );
  }

  function DesktopDigitalCatalogDetailsView() {
    return (
      <>
        <IonContent
          scrollEvents={true}
          onIonScroll={handleScroll}
          className="hk-desktop-digital-catalog-details"
        >
          {taskLoading ? (
            <div className="hk-loading-spinner">
              <IonSpinner name="crescent" color="primary" />
            </div>
          ) : (
            <>
              <div
                className="hk-desktop-digital-catalog-details-header"
                style={!isTitleVisible ? { height: '90px' } : {}}
              >
                <div className="hk-desktop-digital-catalog-details-header-title">
                  {!!productTemplate?.type_group_name
                    ? productTemplate.type_group_name
                    : 'Product Type Name'}
                </div>
                {isTitleVisible && (
                  <div
                    id="hide"
                    className="hk-desktop-digital-catalog-details-header-description"
                  >
                    {!!catalogTaskDetails?.name
                      ? catalogTaskDetails.name
                      : 'Catalog Refrigerator (or Fridge/Freezer, Wine Refrigerator, Beverage Center)'}
                  </div>
                )}
                <IonFab vertical="top" horizontal="end">
                  <IonFabButton
                    color="background"
                    className={`hk-fab-button ${
                      platformType === HKPlatform.DESKTOP && isDesktopWidth
                        ? ''
                        : 'hk-fab-button-margin-top'
                    }`}
                    onClick={() => dispatch(hideCatalogModal())}
                  >
                    <IonIcon color="dark" icon={Close} />
                  </IonFabButton>
                </IonFab>
              </div>
              <form onSubmit={handleSubmit(completeTask)}>
                <div
                  className={`hk-desktop-digital-catalog-details-body ${
                    catalogTaskDetails?.finished === null
                      ? 'hk-desktop-digital-task-details-body-margin-bottom'
                      : ''
                  }`}
                >
                  {!!availableProducts.length && (
                    <div>
                      <HKSelect
                        className="hk-catalog-details-body-select-product"
                        labelClassName="hk-catalog-details-body-select-product-label"
                        label="Select Product"
                        placeholder="Select Product"
                        value={selectedProductId}
                        options={availableProductOptions}
                        onChange={selectProduct}
                      />
                    </div>
                  )}
                  <>
                    {!!selectedProductId
                      ? CurrentCatalogQuestions()
                      : CatalogTaskQuestions()}
                  </>
                  {!!catalogTaskDetails?.tutorial_url && (
                    <div className="hk-desktop-digital-catalog-details-body-video">
                      <div className="hk-desktop-digital-task-details-body-video-title">
                        Video Guide:
                      </div>
                      <div className="hk-desktop-digital-task-details-body-video-content">
                        <div className="video">
                          <>
                            <ReactPlayer
                              width="100%"
                              height="100%"
                              controls={true}
                              url={catalogTaskDetails.tutorial_url}
                            />
                          </>
                        </div>
                        {/* TODO: Add back when video's have descriptions
                    <div className="video-description">
                      <div className="video-description-title">
                        HVAC Air and Fan
                      </div>
                      <div className="video-description-subtitle">
                        We walk you through everything on how to successfully
                        catalog your refrigeration device.
                      </div>
                    </div> */}
                      </div>
                    </div>
                  )}
                  <IonInput
                    name="notes"
                    ref={register}
                    className="hk-desktop-digital-catalog-details-input notes"
                    placeholder="Leave a note about, location, color, etc..."
                    value={getValues('notes')}
                  ></IonInput>
                  {catalogTaskStatusTags?.length !== 0 && (
                    <div className="hk-desktop-digital-catalog-details-body-tags">
                      <div className="hk-desktop-digital-catalog-details-body-tags-title">
                        Add a Tag:
                      </div>
                      <div className="hk-desktop-digital-catalog-details-body-tags-content">
                        {!!catalogTaskStatusTags &&
                          catalogTaskStatusTags.map((tag, i) => {
                            return (
                              <>
                                {tag.digital && (
                                  <div
                                    className={`tag ${
                                      selectedStatusTags.includes(tag.id) &&
                                      'tag-selected'
                                    }`}
                                    key={`tag-${i}`}
                                    onClick={() => selectStatusTag(tag.id)}
                                  >
                                    {tag.name}
                                  </div>
                                )}
                              </>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="hk-desktop-digital-catalog-details-footer">
                  <div className="hk-desktop-digital-catalog-details-footer-button-container">
                    <div
                      className="hk-desktop-digital-catalog-details-footer-no-item"
                      onClick={() => noItem(getValues('notes'))}
                    >
                      No Item
                    </div>
                    <IonButton
                      color="white"
                      className="hk-desktop-digital-catalog-details-footer-button"
                      type="button"
                      disabled={!modelPhoto && !selectedProductId}
                      onClick={() => addProduct(getValues())}
                    >
                      Add Another
                    </IonButton>
                    {catalogTaskDetails?.finished === null && (
                      <IonButton
                        className="hk-desktop-digital-catalog-details-footer-button"
                        color="primary"
                        type="submit"
                        disabled={!modelPhoto || loading}
                      >
                        {loading ? <IonSpinner name="dots" /> : 'Complete Task'}
                      </IonButton>
                    )}
                    {catalogTaskDetails?.finished !== null && (
                      <IonButton
                        className="hk-desktop-digital-catalog-details-footer-button"
                        color="primary"
                        type="submit"
                        disabled={!checkIfTaskEdited() || loading}
                      >
                        {loading ? <IonSpinner name="dots" /> : 'Update Task'}
                      </IonButton>
                    )}
                  </div>
                </div>
              </form>
            </>
          )}
        </IonContent>
      </>
    );
  }

  function MobileDigitalCatalogDetailsView() {
    return (
      <>
        <IonContent
          scrollEvents={true}
          onIonScroll={handleScroll}
          className="hk-digital-catalog-details"
        >
          {taskLoading ? (
            <div className="hk-loading-spinner">
              <IonSpinner name="crescent" color="primary" />
            </div>
          ) : (
            <>
              <div
                className="hk-digital-catalog-details-header"
                style={!isTitleVisible ? { height: '90px' } : {}}
              >
                <div className="hk-digital-catalog-details-header-title">
                  {!!productTemplate?.type_group_name
                    ? productTemplate.type_group_name
                    : 'Product Type Name'}
                </div>
                {isTitleVisible && (
                  <div
                    id="hide"
                    className="hk-digital-catalog-details-header-description"
                  >
                    {!!catalogTaskDetails?.name
                      ? catalogTaskDetails.name
                      : 'Catalog Refrigerator (or Fridge/Freezer, Wine Refrigerator, Beverage Center)'}
                  </div>
                )}
                <IonFab vertical="top" horizontal="end">
                  <IonFabButton
                    color="background"
                    className={`hk-fab-button ${
                      platformType === HKPlatform.DESKTOP && isDesktopWidth
                        ? ''
                        : 'hk-fab-button-margin-top'
                    }`}
                    onClick={() => dispatch(hideCatalogModal())}
                  >
                    <IonIcon color="dark" icon={Close} />
                  </IonFabButton>
                </IonFab>
              </div>
              <form onSubmit={handleSubmit(completeTask)}>
                <div className={`hk-digital-catalog-details-body`}>
                  {!!availableProducts.length && (
                    <div className="hk-digital-catalog-details-body-picker">
                      <HKSelect
                        className="hk-catalog-details-body-select-product"
                        labelClassName="hk-catalog-details-body-select-product-label"
                        label="Select Product"
                        placeholder="Select Product"
                        value={selectedProductId}
                        options={availableProductOptions}
                        onChange={selectProduct}
                      />
                    </div>
                  )}
                  <>
                    {!!selectedProductId
                      ? CurrentCatalogQuestions()
                      : CatalogTaskQuestions()}
                  </>
                  {!!catalogTaskDetails?.tutorial_url && (
                    <div className="hk-digital-catalog-details-body-video">
                      <div className="hk-digital-task-details-body-video-title">
                        Video Guide:
                      </div>
                      <div className="hk-digital-task-details-body-video-content">
                        <div className="video">
                          <>
                            <ReactPlayer
                              width="100%"
                              height="100%"
                              controls={true}
                              url={catalogTaskDetails.tutorial_url}
                            />
                          </>
                        </div>
                        {/* TODO: Add back when video's have descriptions
                        <div className="video-description">
                          <div className="video-description-title">
                            HVAC Air and Fan
                          </div>
                          <div className="video-description-subtitle">
                            We walk you through everything on how to successfully
                            catalog your refrigeration device.
                          </div>
                        </div> */}
                      </div>
                    </div>
                  )}
                  <IonInput
                    name="notes"
                    ref={register}
                    className="hk-digital-catalog-details-input notes"
                    placeholder="Leave a note about, location, color, etc..."
                    type="text"
                    value={getValues('notes')}
                  ></IonInput>
                  {catalogTaskStatusTags?.length !== 0 && (
                    <div className="hk-digital-catalog-details-body-tags">
                      <div className="hk-digital-catalog-details-body-tags-title">
                        Add a Tag:
                      </div>
                      <div className="hk-digital-catalog-details-body-tags-content">
                        {!!catalogTaskStatusTags &&
                          catalogTaskStatusTags.map((tag, i) => {
                            return (
                              <>
                                {tag.digital && (
                                  <div
                                    className={`tag ${
                                      selectedStatusTags.includes(tag.id) &&
                                      'tag-selected'
                                    }`}
                                    key={`tag-${i}`}
                                    onClick={() => selectStatusTag(tag.id)}
                                  >
                                    {tag.name}
                                  </div>
                                )}
                              </>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="hk-digital-catalog-details-footer">
                  <div className="hk-digital-catalog-details-footer-button-container">
                    <IonButton
                      color="white"
                      className="hk-digital-catalog-details-footer-button"
                      type="button"
                      disabled={!modelPhoto && !selectedProductId}
                      onClick={() => addProduct(getValues())}
                    >
                      Add Another
                    </IonButton>
                    {catalogTaskDetails?.finished === null && (
                      <IonButton
                        className="hk-digital-catalog-details-footer-button"
                        color="primary"
                        type="submit"
                        disabled={!modelPhoto || loading}
                      >
                        {loading ? <IonSpinner name="dots" /> : 'Complete Task'}
                      </IonButton>
                    )}
                    {catalogTaskDetails?.finished !== null && (
                      <IonButton
                        className="hk-digital-catalog-details-footer-button"
                        color="primary"
                        type="submit"
                        disabled={!checkIfTaskEdited() || loading}
                      >
                        {loading ? <IonSpinner name="dots" /> : 'Update Task'}
                      </IonButton>
                    )}
                  </div>
                  <div
                    className="hk-digital-catalog-details-footer-no-item"
                    onClick={() => noItem(getValues('notes'))}
                  >
                    No Item
                  </div>
                </div>
              </form>
            </>
          )}
        </IonContent>
      </>
    );
  }

  return (
    <>
      {platformType === HKPlatform.DESKTOP && isDesktopWidth
        ? DesktopDigitalCatalogDetailsView()
        : MobileDigitalCatalogDetailsView()}
    </>
  );
};

export default DigitalCatalogDetails;
